// Components/Billing/PaymentForm.jsx - Updated with Full Name and Email Fields
import React, { useState, useEffect } from 'react';
import { CreditCard, Lock, CheckCircle, AlertCircle, Loader2, Copy, Building, User, Mail } from 'lucide-react';
import { sendBankTransferNotification } from '../../Fetch/fetchSendEmailNotification';
import styles from './PaymentForm.module.css';

// Subscription pricing function
const getSubscriptionPrice = (numApartments) => {
    console.log('PaymentForm - getSubscriptionPrice called with:', numApartments, 'type:', typeof numApartments);
    
    // Convert to number if it's a string
    const apartmentCount = parseInt(numApartments);
    console.log('PaymentForm - Converted to number:', apartmentCount);
    
    if (apartmentCount <= 16) {
        console.log('PaymentForm - Returning small building price: 50');
        return { price: 50, plan: 'בניין קטן' };
    } else if (apartmentCount <= 80) {
        console.log('PaymentForm - Returning medium building price: 70');
        return { price: 70, plan: 'בניין בינוני' };
    } else {
        console.log('PaymentForm - Returning large building price: 120');
        return { price: 120, plan: 'בניין גדול' };
    }
};

// Updated PaymentForm.jsx - Auto-fill logic that matches dropdown display
const PaymentForm = ({ subscription, onPaymentSuccess, onCancel, userProfile, buildingDetails }) => {
    const [paymentData, setPaymentData] = useState({
        paymentMethod: 'BankTransfer',
        transferConfirmed: false,
        address: '',
        fullName: '', // New field
        email: '',    // New field
        // Add separate address components for API
        addressComponents: {
            address: '',
            addressNumber: '',
            city: ''
        }
    });

    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [copiedField, setCopiedField] = useState('');

    // Helper function to get consistent building address display (same as Layout.jsx)
    const getBuildingDisplayAddress = (addressData) => {
        const addressParts = [];
        if (addressData.address) addressParts.push(addressData.address);
        if (addressData.addressNumber) addressParts.push(addressData.addressNumber);
        if (addressData.city) addressParts.push(addressData.city);

        return addressParts.length > 0 ? addressParts.join(' ') : '';
    };

    // State for correct building info (separate from props)
    const [correctBuildingInfo, setCorrectBuildingInfo] = useState(null);
    const [calculatedPrice, setCalculatedPrice] = useState(subscription?.monthlyPrice || 50);

    // Auto-fill all fields when component mounts - UPDATED FOR CONSISTENCY
    useEffect(() => {
        const fillFormFields = () => {
            let address = '';
            let addressComponents = {
                address: '',
                addressNumber: '',
                city: ''
            };
            let buildingInfo = null;
            let fullName = '';
            let email = '';

            // Auto-fill name and email from userProfile or localStorage
            if (userProfile) {
                fullName = userProfile.fullName || userProfile.FullName || userProfile.name || '';
                email = userProfile.email || userProfile.Email || '';
            } else {
                // Try to get from localStorage if userProfile is not available
                try {
                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        const user = JSON.parse(storedUser);
                        fullName = user.fullName || user.FullName || user.name || '';
                        email = user.email || user.Email || '';
                    }
                } catch (error) {
                }
            }

            // Address auto-fill logic (existing logic)
            // First priority: Check for buildingNeedPay data from localStorage (for unpaid buildings)
            const buildingNeedPayStr = localStorage.getItem('buildingNeedPay');
            if (buildingNeedPayStr) {
                try {
                    const buildingNeedPay = JSON.parse(buildingNeedPayStr);

                    // Store separate components
                    addressComponents = {
                        address: buildingNeedPay.address || '',
                        addressNumber: buildingNeedPay.addressNumber || '',
                        city: buildingNeedPay.city || ''
                    };

                    // Use the consistent display address if available, otherwise build it
                    address = buildingNeedPay.displayAddress || getBuildingDisplayAddress(addressComponents);

                    // Store building info for display
                    buildingInfo = {
                        displayAddress: address,
                        numApartments: buildingNeedPay.numApartments,
                        ...addressComponents
                    };

                    // Calculate price based on apartment count
                    if (buildingNeedPay.numApartments) {
                        const priceInfo = getSubscriptionPrice(buildingNeedPay.numApartments);
                        setCalculatedPrice(priceInfo.price);
                        console.log('PaymentForm - Price calculated from buildingNeedPay:', priceInfo);
                    }

                } catch (error) {
                }
            }

            // Second priority: Try to get from current user's selected building info (most reliable)
            if (!address) {
                try {
                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        const user = JSON.parse(storedUser);
                        if (user.selectedBuildingInfo) {
                            const selectedBuildingInfo = user.selectedBuildingInfo;

                            // Use displayAddress if available (from updated localStorage)
                            if (selectedBuildingInfo.displayAddress) {
                                address = selectedBuildingInfo.displayAddress;
                                addressComponents = {
                                    address: selectedBuildingInfo.address || '',
                                    addressNumber: selectedBuildingInfo.addressNumber || '',
                                    city: selectedBuildingInfo.city || ''
                                };
                            } else {
                                // Fallback to building components
                                addressComponents = {
                                    address: selectedBuildingInfo.address || '',
                                    addressNumber: selectedBuildingInfo.addressNumber || '',
                                    city: selectedBuildingInfo.city || ''
                                };
                                address = getBuildingDisplayAddress(addressComponents);
                            }

                            if (address) {
                                // Store building info for display
                                buildingInfo = {
                                    displayAddress: address,
                                    numApartments: selectedBuildingInfo.numApartments,
                                    ...addressComponents
                                };

                                // Calculate price based on apartment count
                                if (selectedBuildingInfo.numApartments) {
                                    const priceInfo = getSubscriptionPrice(selectedBuildingInfo.numApartments);
                                    setCalculatedPrice(priceInfo.price);
                                    console.log('PaymentForm - Price calculated from selectedBuildingInfo:', priceInfo);
                                }
                            }
                        }
                    }
                } catch (error) {
                }
            }

            // Third priority: Try userProfile address fields (avoid using buildingName)
            if (!address && userProfile) {
                // Try different possible address fields in userProfile
                const possibleAddresses = [
                    userProfile.address,
                    userProfile.Address,
                    userProfile.buildingAddress,
                ];

                const foundAddress = possibleAddresses.find(addr => addr && addr.trim());
                if (foundAddress) {
                    address = foundAddress.trim();

                    // Try to parse the address into components if it's a combined string
                    const addressParts = address.split(' ');
                    if (addressParts.length >= 2) {
                        addressComponents = {
                            address: addressParts.slice(0, -2).join(' ') || addressParts[0] || '',
                            addressNumber: addressParts[addressParts.length - 2] || '',
                            city: addressParts[addressParts.length - 1] || ''
                        };
                    } else {
                        addressComponents.address = address;
                    }
                }
            }

            // Fourth priority: buildingDetails - but AVOID buildingName as it's often incorrect
            if (!address && buildingDetails) {
                const buildingAddress = buildingDetails.buildingAddress || buildingDetails.address;
                // Explicitly avoid buildingDetails.buildingName as it's often wrong
                if (buildingAddress && buildingAddress !== buildingDetails.buildingName) {
                    const buildingComponents = {
                        address: buildingDetails.address || '',
                        addressNumber: buildingDetails.addressNumber || '',
                        city: buildingDetails.city || ''
                    };

                    address = getBuildingDisplayAddress(buildingComponents) || buildingAddress;
                    addressComponents = buildingComponents;

                }
            }

            // Don't auto-fill if user already has values (user might have typed correct info)
            const shouldUpdateAddress = !paymentData.address || !paymentData.address.trim();
            const shouldUpdateName = !paymentData.fullName || !paymentData.fullName.trim();
            const shouldUpdateEmail = !paymentData.email || !paymentData.email.trim();

            // Update state with all auto-filled values
            setPaymentData(prev => ({
                ...prev,
                ...(shouldUpdateAddress && { address: address || '' }),
                ...(shouldUpdateName && { fullName: fullName || '' }),
                ...(shouldUpdateEmail && { email: email || '' }),
                addressComponents: addressComponents
            }));

            // Set correct building info for display
            setCorrectBuildingInfo(buildingInfo);

            // Log warning if no address was found
            if (!address) {
            }
        };

        fillFormFields();
    }, [userProfile, buildingDetails]); // Remove paymentData fields from dependency to avoid infinite loops

    // Rest of the component setup...
    const bankDetails = {
        bankName: 'בנק הפועלים',
        bankNumber: '12',
        branchNumber: '638',
        accountNumber: '367397',
        accountName: 'הילה ומוטי שגב'
    };

    const copyToClipboard = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(''), 2000);
        } catch (err) {
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPaymentData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
    };

    const validateForm = () => {
        // Check required name field
        if (!paymentData.fullName || !paymentData.fullName.trim()) {
            setError('יש למלא את שדה השם המלא');
            return false;
        }

        if (paymentData.fullName.trim().length < 2) {
            setError('יש להכניס שם מלא תקין (לפחות 2 תווים)');
            return false;
        }

        // Check required email field
        if (!paymentData.email || !paymentData.email.trim()) {
            setError('יש למלא את שדה האימייל');
            return false;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(paymentData.email.trim())) {
            setError('יש להכניס כתובת אימייל תקינה');
            return false;
        }

        // Check required address field
        const correctAddress = paymentData.address && paymentData.address.trim();
        if (!correctAddress) {
            setError('יש למלא את שדה הכתובת. אנא הכנס את כתובת הבניין המלאה כולל עיר.');
            return false;
        }

        // Ensure we have a meaningful address (not just spaces)
        if (correctAddress.length < 3) {
            setError('יש להכניס כתובת תקינה. אנא הכנס את כתובת הבניין המלאה כולל עיר.');
            return false;
        }

        // Check confirmation checkbox
        if (!paymentData.transferConfirmed) {
            setError('יש לאשר כי ביצעת את ההעברה הבנקאית');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setIsProcessing(true);

        try {
            // Send notification to management with the correct information
            try {
                // Use the PaymentForm inputs as the primary source (correct values)
                const correctAddress = paymentData.address.trim();
                const correctName = paymentData.fullName.trim();
                const correctEmail = paymentData.email.trim();

                const combinedAddress = correctAddress ||
                    getBuildingDisplayAddress(paymentData.addressComponents);

                const transferNotificationData = {
                    tenantName: correctName, // Use form input
                    tenantEmail: correctEmail, // Use form input
                    apartmentNumber: combinedAddress, // Use the actual address
                    address: paymentData.addressComponents.address,
                    addressNumber: paymentData.addressComponents.addressNumber,
                    city: paymentData.addressComponents.city,
                    amount: calculatedPrice || 0,
                    subscriptionDetails: `מנוי חודשי - ${subscription?.subscriptionType || 'רגיל'} עבור כתובת: ${combinedAddress}`
                };

                await sendBankTransferNotification(transferNotificationData);
            } catch (notificationError) {
                // Don't stop the process if notification fails
            }

            // Clean up buildingNeedPay on successful confirmation
            localStorage.removeItem('buildingNeedPay');

            setSuccess(true);
            setTimeout(() => {
                onPaymentSuccess && onPaymentSuccess({ success: true, method: 'BankTransfer' });
            }, 2000);

        } catch (error) {
            setError(error.message || 'שגיאה בעיבוד הבקשה. אנא נסה שוב.');
        } finally {
            setIsProcessing(false);
        }
    };

    if (success) {
        return (
            <div className={styles.paymentForm} dir="rtl">
                <div className={styles.successMessage}>
                    <CheckCircle className={styles.successIcon} />
                    <h3>בקשת התשלום התקבלה בהצלחה!</h3>
                    <p>ההעברה תטופל תוך 24 שעות. המנוי יופעל לאחר קבלת האישור.</p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        🏦 הודעה נשלחה לועד הבית עם פרטי ההעברה הבנקאית
                    </p>
                    {correctBuildingInfo && (
                        <p style={{ fontSize: '14px', color: '#4caf50', marginTop: '5px', fontWeight: 'bold' }}>
                            🏢 עבור הבניין: {correctBuildingInfo.displayAddress}
                        </p>
                    )}
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        <p>👤 שם: {paymentData.fullName}</p>
                        <p>📧 אימייל: {paymentData.email}</p>
                        <p>📍 כתובת: {paymentData.address}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.paymentForm} dir="rtl">
            <div className={styles.formHeader}>
                <h3 className={styles.formTitle}>
                    <Building className={styles.titleIcon} />
                    פרטי תשלום - העברה בנקאית
                </h3>
                <div className={styles.securityBadge}>
                    <Lock size={16} />
                    תשלום מאובטח
                </div>
            </div>

            {/* Building Info Display - use correct building info from same source as input */}
            {correctBuildingInfo && (
                <div className={styles.buildingInfo}>
                    <h4 className={styles.sectionTitle}>🏢 פרטי הבניין:</h4>
                    <p className={styles.buildingName}>
                        {correctBuildingInfo.displayAddress}
                    </p>
                </div>
            )}

            {/* Payment Summary */}
            <div className={styles.paymentSummary}>
                <div className={styles.summaryRow}>
                    <span>מנוי חודשי:</span>
                    <span className={styles.price}>{calculatedPrice} שח</span>
                </div>
                <div className={styles.summaryRow}>
                    <span>סה"כ לתשלום:</span>
                    <span className={styles.totalPrice}>{calculatedPrice} שח</span>
                </div>
            </div>

            {/* Customer Information Section - NEW */}
            <div className={styles.customerSection}>
                <h4 className={styles.sectionTitle}>👤 פרטי לקוח:</h4>
                <div className={styles.customerForm}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <User size={16} className={styles.labelIcon} />
                                שם מלא*
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={paymentData.fullName}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder="הכנס שם מלא"
                                required
                                disabled={isProcessing}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <Mail size={16} className={styles.labelIcon} />
                                אימייל*
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={paymentData.email}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder="הכנס כתובת אימייל"
                                required
                                disabled={isProcessing}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Address Information Section */}
            <div className={styles.addressSection}>
                <h4 className={styles.sectionTitle}>📍 פרטי כתובת:</h4>
                <div className={styles.addressForm}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            <Building size={16} className={styles.labelIcon} />
                            כתובת מלאה*
                        </label>
                        <input
                            type="text"
                            name="address"
                            value={paymentData.address}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="הכנס את כתובת מלאה"
                            required
                            disabled={isProcessing}
                        />
                        <small className={styles.fieldHint}>
                            כתובת זו צריכה להתאים לבניין שנבחר בתפריט העליון
                        </small>
                    </div>
                </div>
            </div>

            {/* Bank Transfer Information */}
            <div className={styles.bankTransferInfo}>
                <h4 className={styles.sectionTitle}>פרטי חשבון לעברת הכסף:</h4>

                <div className={styles.bankDetails}>
                    <div className={styles.bankDetailRow}>
                        <span className={styles.detailLabel}>שם בנק:</span>
                        <div className={styles.detailValue}>
                            <span>{bankDetails.bankName}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bankDetails.bankName, 'bankName')}
                                className={styles.copyButton}
                                title="העתק"
                            >
                                {copiedField === 'bankName' ? (
                                    <CheckCircle size={16} className={styles.copiedIcon} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className={styles.bankDetailRow}>
                        <span className={styles.detailLabel}>מספר בנק:</span>
                        <div className={styles.detailValue}>
                            <span className={styles.monoText}>{bankDetails.bankNumber}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bankDetails.bankNumber, 'bankNumber')}
                                className={styles.copyButton}
                                title="העתק"
                            >
                                {copiedField === 'bankNumber' ? (
                                    <CheckCircle size={16} className={styles.copiedIcon} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className={styles.bankDetailRow}>
                        <span className={styles.detailLabel}>מספר סניף:</span>
                        <div className={styles.detailValue}>
                            <span className={styles.monoText}>{bankDetails.branchNumber}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bankDetails.branchNumber, 'branchNumber')}
                                className={styles.copyButton}
                                title="העתק"
                            >
                                {copiedField === 'branchNumber' ? (
                                    <CheckCircle size={16} className={styles.copiedIcon} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className={styles.bankDetailRow}>
                        <span className={styles.detailLabel}>מספר חשבון:</span>
                        <div className={styles.detailValue}>
                            <span className={styles.monoText}>{bankDetails.accountNumber}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bankDetails.accountNumber, 'accountNumber')}
                                className={styles.copyButton}
                                title="העתק"
                            >
                                {copiedField === 'accountNumber' ? (
                                    <CheckCircle size={16} className={styles.copiedIcon} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className={styles.bankDetailRow}>
                        <span className={styles.detailLabel}>שם חשבון:</span>
                        <div className={styles.detailValue}>
                            <span>{bankDetails.accountName}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bankDetails.accountName, 'accountName')}
                                className={styles.copyButton}
                                title="העתק"
                            >
                                {copiedField === 'accountName' ? (
                                    <CheckCircle size={16} className={styles.copiedIcon} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.instructions}>
                <h4 className={styles.sectionTitle}>הוראות חשובות:</h4>
                <ul className={styles.instructionsList}>
                    <li>וודאו שהעברתם את הסכום הנכון: {calculatedPrice} שח</li>
                    <li>ציינו במטרת התשלום: "מנוי אפליקציה - {paymentData.fullName} - {paymentData.address || 'בניין'}"</li>
                    <li>שמרו את האישור על ההעברה לצורך מעקב</li>
                    <li>הפעלת המנוי תתבצע תוך 24 שעות לאחר קבלת האישור</li>
                    <li>🏦 לאחר אישור ההעברה, הודעה תישלח אוטומטית לועד הבית</li>
                </ul>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.confirmationGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="transferConfirmed"
                            checked={paymentData.transferConfirmed}
                            onChange={handleInputChange}
                            className={styles.checkbox}
                            disabled={isProcessing}
                        />
                        <span className={styles.checkboxText}>
                            אני מאשר/ת כי ביצעתי את ההעברה הבנקאית בסכום של {calculatedPrice} שח עבור {paymentData.address || 'הבניין'}
                        </span>
                    </label>
                </div>

                <div className={styles.formActions}>
                    <button
                        type="submit"
                        className={styles.payButton}
                        disabled={
                            isProcessing || 
                            !paymentData.transferConfirmed || 
                            !(paymentData.address && paymentData.address.trim()) ||
                            !(paymentData.fullName && paymentData.fullName.trim()) ||
                            !(paymentData.email && paymentData.email.trim())
                        }
                    >
                        {isProcessing ? (
                            <>
                                <Loader2 className={styles.spinner} />
                                מעבד בקשה...
                            </>
                        ) : (
                            <>
                                <CheckCircle size={16} />
                                אישור ביצוע העברה
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={isProcessing}
                    >
                        ביטול
                    </button>
                </div>
            </form>

            <div className={styles.securityInfo}>
                <Lock size={14} />
                <span>כל המידע מוצפן ומאובטח באמצעות SSL</span>
            </div>
        </div>
    );
};

export default PaymentForm;